import {
	computed, defineAsyncComponent, ref, 
} from "vue";

import { defineStore } from "pinia";
import { useRouter } from "vue-router";

import { type UserIdentitySchema } from "@ca/db/schema";

import { client } from "@/api";

const initialUrl = () => {
	const path = window.location.pathname;
	const search = window.location.search;
	return path + search;
};

const startPath = initialUrl() || "/";

export const useAuthStore = defineStore("auth", () => {
	const session = ref<null | Omit<UserIdentitySchema, "settings">>(null);
	const fleetBoardOnly = ref(false);
	const loginErr = ref("");
	const checking = ref(false);
	const router = useRouter();
	const login = async ({ email, password }: { email: string, password: string }) => {
		loginErr.value = "";
		const res = await client.api.v1.sessions.post({ email, password });
		if (res.status !== 200) {
			const errMap = {
				401: "Invalid login credentials",
				500: "An unknown error occurred",
			};
			loginErr.value = errMap[res.status as keyof typeof errMap] || "An unknown error occurred";
		}
		if (res.data?.user) {
			session.value = res.data.user;
			fleetBoardOnly.value = res.data.fleetBoardOnly;
			router.push(startPath);
		}
	};

	const isPublicRoute = () => {
		const path = window.location.pathname;
		return path.startsWith("/reset-password")
            || path.startsWith("/setup-account")
            || path.startsWith("/formgen/");
	};

	const isPilotAllowedRoute = () => {
		const path = window.location.pathname;
		return path.startsWith("/reset-password");
	};


	const rootComponent = computed(() => {
		if (session.value && fleetBoardOnly.value) {
			// The page-load path: after signing in from /login, the board the TV was opened on.
			return startPath.split("?")[0]?.replace(/\/+$/, "").toLowerCase() === "/uw-board"
				? defineAsyncComponent(() => import("../views/UwBoard/UwBoardView.vue"))
				: defineAsyncComponent(() => import("../views/FleetBoard/FleetBoardView.vue"));
		}
		const hasPilotOnlyScope = session.value?.scopes?.length === 1
			&& session.value.scopes[0] === "pilot";
		if (hasPilotOnlyScope && !isPilotAllowedRoute()) {
			return defineAsyncComponent(() => import("../views/PilotOnlyWebView.vue"));
		}
		if (session.value?.email || isPublicRoute()) {
			return defineAsyncComponent(() => import("../views/AuthenticatedView.vue"));
		}
		if (!session.value && !checking.value) {
			const LoginView = defineAsyncComponent(() => 
				import("../views/LoginView.vue"),
			);
			return LoginView;
		}
		return null;
	});


	const getCurrentSession = async () => {
		if (isPublicRoute()) {
			return;
		}
		checking.value = true;
		const res = await client.api.v1.sessions._current.get();
		if (res.status === 200 && res.data?.user) {
			session.value = res.data.user as UserIdentitySchema;
			fleetBoardOnly.value = res.data.fleetBoardOnly;
			router.push(startPath);
		} else {
			router.push("/login");
		}
		checking.value = false;
	};

	const logout = async () => {
		session.value = null;
		fleetBoardOnly.value = false;
		await client.api.v1.sessions._current.delete();
		try {
			window.postMessage({ type: "classa-extension-logout" }, window.location.origin);
		} catch {
			// ignore
		}
	};
	return {
		fleetBoardOnly,
		getCurrentSession,
		login,
		logout,
		rootComponent,
		session,
		loginErr,
	};
});
